















import {Component, Prop, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import WidgetService from '@/services/widget.service';
import {MarkerClusterer} from '@googlemaps/markerclusterer';

@Component
export default class MiniMap extends Vue {

  private get spreadingFactorColors() {
    return [
      { sf: 7, color: '#c9e52f' },
      { sf: 8, color: '#a6d75b' },
      { sf: 9, color: '#76c68f' },
      { sf: 10, color: '#48b5c4' },
      { sf: 11, color: '#22a7f0' },
      { sf: 12, color: '#1984c5' },
    ];
  }
  @Action private addToastErrorMessage: any;

  @Prop({ default: '100%' }) private height!: string;
  @Prop() private devices!: any[];
  @Prop() private gateways!: any[];
  @Prop({ default: false }) private interactive!: boolean;
  @Prop({ default: () => [51.5072, -0.1276] }) private initialCoords!: number[];
  @Prop({ default: null }) private searchInputRef!: any;
  @Getter private hasPermission!: any;

  private isLoading: boolean = true;
  private readonly DEFAULT_ZOOM = 15;

  private map: any;
  private AdvancedMarkerElement: any;
  private geocoder: google.maps.Geocoder | null = null;
  private singleMarker: google.maps.Marker | null = null;

  private allDeviceMarkers: Array<any & { locationData?: any[] }> = [];
  private gatewayMarkers: { [key: string]: any } = {};
  private markerClusterer: any | null = null;
  private allMarkers: any[] = [];
  private locations: any[] = [];
  private hasGatewaySettings: boolean = false;
  private noMapData: boolean = false;

  public setLocation(coords: number[]) {
    const newLatLng = new google.maps.LatLng(coords[0], coords[1]);
    this.map.setCenter(newLatLng);
    this.updateLocationAndEmit(newLatLng);
  }

  private async mounted() {
    this.$emit('mapLoading', true);
    await this.checkForLoraSettings();
    await this.loadAllData();
    await this.$nextTick();

    if (!this.allMarkers.length) {
      this.isLoading = false;
      this.noMapData = true;
      return;
    }

    await this.createMap();
  }

  private async createMap() {
    if (!this.$refs.miniMap) {
      this.$emit('mapLoading', false);
      this.isLoading = false;
      return;
    }

    try {
      const { Map } = await (google.maps as any).importLibrary('maps') as any;
      const { AdvancedMarkerElement } = await (google.maps as any).importLibrary('marker') as any;
      this.AdvancedMarkerElement = AdvancedMarkerElement;

      const mapOptions = {
        mapTypeId: 'roadmap', fullscreenControl: false,
        streetViewControl: false, mapId: 'afe3e0e97cbe7a49b1f20df9', clickableIcons: false,
        gestureHandling: 'greedy', keyboardShortcuts: false,
      };

      this.map = new Map(this.$refs.miniMap as HTMLElement, mapOptions);

      if (this.interactive) {
        this.initInteractiveFeatures();
      } else {
        await this.applyFiltersAndRedraw();
      }
      this.$emit('mapLoaded');
    } catch (e) {
      const err = e as Error;
      this.addToastErrorMessage(err.message);
    } finally {
      this.$emit('mapLoading', false);
      this.isLoading = false;
    }
  }

  private async loadAllData() {
    if (this.devices || this.gateways) {
      const devMarkers = this.devices ? [...this.devices] : [];
      const gwayMarkers = this.gateways ? [...this.gateways] : [];
      this.allMarkers = [...devMarkers, ...gwayMarkers];
    } else {
      try {
        const markers = [];
        markers.push(WidgetService.getDeviceMarkers());
        if (this.hasGatewaySettings) {
          markers.push(WidgetService.getGatewayMarkers());
        }
        const [devMarkers, gwayMarkers] = await Promise.all(markers);
        this.allMarkers = [...(devMarkers || []), ...(gwayMarkers || [])];
      } catch (e) {
        const err = e as Error;
        this.addToastErrorMessage(err.message);
      }
    }
    this.applyFiltersAndRedraw();
  }

  private async checkForLoraSettings(): Promise<void> {
    try {
      if (this.hasPermission('gateway:read') || this.hasPermission('inventory:read')) {
        const hasGatewaySettings = await WidgetService.hasGatewaySettings();
        this.hasGatewaySettings = hasGatewaySettings.result;
      } else {
        this.hasGatewaySettings = false;
      }
      this.$emit('hasGatewaySettings', this.hasGatewaySettings);
    } catch (e) {
      const err = e as Error;
      this.addToastErrorMessage(err.message);
    }
  }

  private initInteractiveFeatures() {
    const initialLatLng = new google.maps.LatLng(this.initialCoords[0], this.initialCoords[1]);
    this.map.setCenter(initialLatLng);
    this.map.setZoom(this.DEFAULT_ZOOM);

    this.singleMarker = new google.maps.Marker({
      position: initialLatLng,
      map: this.map,
      draggable: true,
    });

    this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) { this.updateLocationAndEmit(e.latLng); }
    });

    this.singleMarker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) { this.updateLocationAndEmit(e.latLng); }
    });

    if (this.searchInputRef) {
      const inputElement = this.searchInputRef.$refs.input;
      const searchBox = new google.maps.places.Autocomplete(inputElement, { fields: ['formatted_address', 'name', 'geometry'] });
      searchBox.bindTo('bounds', this.map);
      searchBox.addListener('place_changed', () => {
        const place = searchBox.getPlace();
        if (place.geometry && place.geometry.location) {
          this.map.fitBounds(place.geometry.viewport || place.geometry.location);
          this.updateLocationAndEmit(place.geometry.location, place.formatted_address);
        }
      });
    }
  }

  private updateLocationAndEmit(latLng: google.maps.LatLng, knownAddress?: string) {
    if (!this.geocoder) { this.geocoder = new google.maps.Geocoder(); }
    if (!this.singleMarker) { return; }

    this.singleMarker.setPosition(latLng);
    this.map.panTo(latLng);

    const emitChangeEvent = (address: string) => {
      this.$emit('location-changed', { lat: latLng.lat(), lng: latLng.lng(), address });
    };

    if (knownAddress) {
      emitChangeEvent(knownAddress);
    } else {
      this.geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          emitChangeEvent(results[0].formatted_address);
        }
      });
    }
  }

  private applyFiltersAndRedraw() {
    if (this.interactive || !this.map) { return; }
    this.locations = [...this.allMarkers];
    this.setMapOptions();
  }

  private async setMapOptions(resetBounds: boolean = true) {
    this.clearMap();
    if (!this.map || !this.locations.length) { return; }

    const locationGroups = this.groupLocations(this.locations);

    for (const group of Object.values(locationGroups) as any[][]) {
      const hasDevices = group.some((item) => item.type === 'device');
      const hasGateways = group.some((item) => item.type === 'gateway');

      if (hasDevices && hasGateways) {
        const marker = this.generateCombinedPin(group);
        this.allDeviceMarkers.push(marker);
      } else if (hasDevices) {
        const marker = this.generateDevicePin(group);
        this.allDeviceMarkers.push(marker);
      } else if (hasGateways) {
        this.generateGatewayMarkers(this.map, group);
      }
    }

    const renderer: any = this.createClusterRenderer();
    this.markerClusterer = new MarkerClusterer({
      map: this.map,
      markers: this.allDeviceMarkers,
      renderer,
      algorithmOptions: { maxZoom: 8 },
    });

    if (resetBounds) {
      this.fitBounds();
    }
  }

  private groupLocations(locations: any[]) {
    return locations.reduce((acc, loc) => {
      if (!loc.lat || !loc.lng) { return acc; }
      const key = `${loc.lat},${loc.lng}`;
      if (!acc[key]) { acc[key] = []; }
      acc[key].push(loc);
      return acc;
    }, {});
  }

  private clearMap() {
    if (this.markerClusterer) { this.markerClusterer.clearMarkers(); }
    Object.values(this.gatewayMarkers).forEach((m) => m.map = null);
    this.allDeviceMarkers = [];
    this.gatewayMarkers = {};
  }

  private fitBounds() {
    if (!this.locations.length) { return; }

    const bounds = new google.maps.LatLngBounds();
    this.locations.forEach((loc: {lat: string; lng: string}) => {
      if (loc.lat && loc.lng) { bounds.extend(new google.maps.LatLng(Number(loc.lat), Number(loc.lng))); }
    });

    if (this.locations.length === 1 || (bounds.getNorthEast() && bounds.getNorthEast().equals(bounds.getSouthWest()))) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(this.DEFAULT_ZOOM);
    } else {
      this.map.fitBounds(bounds);
    }
  }

  private generateDevicePin(locations: any[]) {
    const sortedLocations = this.sortLocationsForDisplay(locations);
    const location = sortedLocations[0];
    let markerSVG;

    if (locations.length > 1) {
      markerSVG = this.createStackedDeviceMarkerSVG(sortedLocations);
    } else {
      const statusColor = this.getDeviceStatusColor(location.status);
      const sfColor = this.getSpreadingFactorColor(location.network.loraSpreadingFactor);
      markerSVG = this.createDeviceMarkerSVG(statusColor, sfColor);
    }

    const marker = new this.AdvancedMarkerElement({
      position: { lat: Number(location.lat), lng: Number(location.lng) },
      content: markerSVG,
      title: `${locations.length} device(s) at this location`,
    });
    (marker as any).locationData = locations;
    return marker;
  }

  private generateCombinedPin(locations: any[]) {
    const sortedLocations = this.sortLocationsForDisplay(locations);
    const markerSVG = this.createCombinedMarkerSVG(sortedLocations);
    const marker = new this.AdvancedMarkerElement({
      position: { lat: Number(locations[0].lat), lng: Number(locations[0].lng) },
      content: markerSVG,
      title: `${locations.length} items at this location`,
    });
    (marker as any).locationData = locations;
    return marker;
  }

  private generateGatewayMarkers(map: google.maps.Map, locations: any[]) {
    const sortedLocations = this.sortLocationsForDisplay(locations);
    const location = sortedLocations[0];
    let markerContent;

    if (locations.length > 1) {
      markerContent = this.createStackedGatewayMarkerSVG(sortedLocations);
    } else {
      markerContent = this.createGatewayIcon(this.getGatewayStatusColor(location.status));
    }

    const marker = new this.AdvancedMarkerElement({
      map,
      position: { lat: Number(location.lat), lng: Number(location.lng) },
      content: markerContent,
      title: `${locations.length} gateway(s) at this location`,
    });

    (marker as any).locationData = locations;
    locations.forEach((gw) => {
      const id = gw.gatewayEui || gw.stationName || gw.id;
      if (id) {
        this.gatewayMarkers[id] = marker;
      }
    });
  }

  private createStackedDeviceMarkerSVG(devices: any[], overrideTopDevice?: any): SVGElement {
    let deviceForColor = devices[0];
    if (overrideTopDevice) {
      deviceForColor = overrideTopDevice;
    }

    const statusColor = this.getDeviceStatusColor(deviceForColor.status);
    const spreadingFactor = deviceForColor.network.loraSpreadingFactor;
    const innerColor = spreadingFactor !== undefined
        ? this.getSpreadingFactorColor(spreadingFactor)
        : statusColor;

    const count = devices.length;
    const size = count >= 100 ? '48' : count >= 10 ? '32' : '24';
    const fontSize = '14px';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.style.transform = 'translateY(50%)';

    const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    outerRing.setAttribute('cx', '16');
    outerRing.setAttribute('cy', '16');
    outerRing.setAttribute('r', '15');
    outerRing.setAttribute('fill', statusColor);
    outerRing.setAttribute('opacity', '0.6');

    const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerCircle.setAttribute('cx', '16');
    innerCircle.setAttribute('cy', '16');
    innerCircle.setAttribute('r', '11');
    innerCircle.setAttribute('fill', innerColor);

    svg.appendChild(outerRing);
    svg.appendChild(innerCircle);

    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('x', '16');
    textElement.setAttribute('y', '17');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', fontSize);
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', this.getContrastingTextColor(innerColor));
    textElement.textContent = count.toString();

    svg.appendChild(textElement);
    return svg;
  }

  private createCombinedMarkerSVG(group: any[], overrideItem?: any): SVGElement {
    const devices = group.filter((item) => item.type === 'device');
    const gateways = group.filter((item) => item.type === 'gateway');
    const itemForColor = overrideItem || devices[0] || gateways[0];
    const statusColor = this.getDeviceStatusColor(itemForColor.status);
    const innerColor = this.getSpreadingFactorColor(itemForColor.network.loraSpreadingFactor);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '36');
    svg.setAttribute('height', '36');
    svg.setAttribute('viewBox', '0 0 36 36');
    svg.style.transform = 'translateY(50%)';

    const gatewayGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    gatewayGroup.setAttribute('transform', 'translate(2, 2) rotate(45 16 16)');
    const outerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    outerRect.setAttribute('x', '6');
    outerRect.setAttribute('y', '6');
    outerRect.setAttribute('width', '24');
    outerRect.setAttribute('height', '24');
    outerRect.setAttribute('fill', statusColor);
    outerRect.setAttribute('opacity', '0.6');
    gatewayGroup.appendChild(outerRect);
    svg.appendChild(gatewayGroup);

    const deviceGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    deviceGroup.setAttribute('transform', 'translate(4, 4)');
    const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    outerRing.setAttribute('cx', '14');
    outerRing.setAttribute('cy', '14');
    outerRing.setAttribute('r', '12');
    outerRing.setAttribute('fill', statusColor);
    outerRing.setAttribute('opacity', '0.8');
    const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerCircle.setAttribute('cx', '14');
    innerCircle.setAttribute('cy', '14');
    innerCircle.setAttribute('r', '9');
    innerCircle.setAttribute('fill', innerColor);
    deviceGroup.appendChild(outerRing);
    deviceGroup.appendChild(innerCircle);
    svg.appendChild(deviceGroup);

    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('x', '18');
    textElement.setAttribute('y', '18');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', '12px');
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', this.getContrastingTextColor(innerColor));
    textElement.textContent = `${group.length}`;
    svg.appendChild(textElement);
    return svg;
  }

  private createDeviceMarkerSVG(statusColor: string, sFColor: string): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.style.transform = 'translateY(50%)';

    const innerColor = !!sFColor ? sFColor : 'rgba(255,255,255,0.6)';

    const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    outerRing.setAttribute('cx', '12');
    outerRing.setAttribute('cy', '12');
    outerRing.setAttribute('r', '10');
    outerRing.setAttribute('fill', statusColor);
    outerRing.setAttribute('opacity', '0.6');

    const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerCircle.setAttribute('cx', '12');
    innerCircle.setAttribute('cy', '12');
    innerCircle.setAttribute('r', '6');
    innerCircle.setAttribute('fill', innerColor);

    svg.appendChild(outerRing);
    svg.appendChild(innerCircle);
    return svg;
  }

  private createStackedGatewayMarkerSVG(gateways: any[], overrideTopGateway?: any): SVGElement {
    let gatewayForColor = gateways[0];
    if (overrideTopGateway) {
      gatewayForColor = overrideTopGateway;
    }
    const statusColor = this.getGatewayStatusColor(gatewayForColor.status);
    const count = gateways.length;
    const fontSize = '14px';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.style.transform = 'translateY(50%)';

    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('transform', 'rotate(45 16 16)');
    const outerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    outerRect.setAttribute('x', '6');
    outerRect.setAttribute('y', '6');
    outerRect.setAttribute('width', '24');
    outerRect.setAttribute('height', '24');
    outerRect.setAttribute('fill', statusColor);
    outerRect.setAttribute('opacity', '0.6');
    const innerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    innerRect.setAttribute('x', '10');
    innerRect.setAttribute('y', '10');
    innerRect.setAttribute('width', '16');
    innerRect.setAttribute('height', '16');
    innerRect.setAttribute('fill', 'rgba(255,255,255,0.6)');
    group.appendChild(outerRect);
    group.appendChild(innerRect);
    svg.appendChild(group);

    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('x', '16');
    textElement.setAttribute('y', '20');
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-weight', 'bold');
    textElement.setAttribute('font-size', fontSize);
    textElement.setAttribute('font-family', 'sans-serif');
    textElement.setAttribute('fill', this.getContrastingTextColor(statusColor));
    textElement.textContent = count.toString();
    svg.appendChild(textElement);
    return svg;
  }

  private createGatewayIcon(fillColor: string): HTMLElement {
    const container = document.createElement('div');
    container.style.width = `28px`;
    container.style.height = `28px`;
    const diamondSVG = this.createGatewayMarkerSVG(fillColor);
    container.appendChild(diamondSVG);
    return container;
  }

  private createGatewayMarkerSVG(statusColor: string): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.style.transform = 'translateY(50%)';

    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('transform', 'rotate(45 16 16) scale(0.9)');
    const outerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    outerRect.setAttribute('x', '6');
    outerRect.setAttribute('y', '6');
    outerRect.setAttribute('width', '24');
    outerRect.setAttribute('height', '24');
    outerRect.setAttribute('fill', statusColor);
    outerRect.setAttribute('opacity', '0.6');
    const innerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    innerRect.setAttribute('x', '10');
    innerRect.setAttribute('y', '10');
    innerRect.setAttribute('width', '16');
    innerRect.setAttribute('height', '16');
    innerRect.setAttribute('fill', 'rgba(255,255,255,0.6)');
    group.appendChild(outerRect);
    group.appendChild(innerRect);
    svg.appendChild(group);
    return svg;
  }

  private createClusterRenderer() {
    return {
      render: ({ count, position, markers }: {
        count: number;
        position: google.maps.LatLngLiteral;
        markers: Array<any & { locationData?: any[] }>;
      }) => {
        if (markers && markers.length > 1) {
          const totalDeviceCount = markers.reduce((acc: any, marker: any) => acc + ((marker as any).locationData && (marker as any).locationData.length || 0), 0);
          return new this.AdvancedMarkerElement({
            position,
            content: this.createClusterIcon(totalDeviceCount),
            zIndex: 1000 + count,
          });
        }
        return markers ? markers[0] : new this.AdvancedMarkerElement({ position });
      },
    };
  }

  private createClusterIcon(count: number): HTMLElement {
    const size = count >= 1000 ? 64 : count >= 100 ? 54 : 44;
    const fontSize = count >= 100 ? 15 : 14;
    const svg = window.btoa(`<svg fill="#016EA1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><circle cx="120" cy="120" r="70" opacity=".6"/><circle cx="120" cy="120" r="60" /></svg>`);
    const clusterIcon = document.createElement('img');
    clusterIcon.src = `data:image/svg+xml;base64,${svg}`;
    clusterIcon.style.cssText = `position: absolute; top: 50%; left: 50%; width: ${size}px; height: ${size}px; transform: translate(-50%, -50%);`;
    const label = document.createElement('div');
    label.textContent = String(count);
    label.style.cssText = `position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-family: Arial, sans-serif; font-size: ${fontSize}px; font-weight: bold;`;
    const container = document.createElement('div');
    container.appendChild(clusterIcon);
    container.appendChild(label);
    return container;
  }

  private sortLocationsForDisplay(locations: any[]): any[] {
    const statusOrder: { [key: string]: number } = { fault: 1, inactive: 2, active: 3 };
    return [...locations].sort((a, b) => {
      const siteA = a.siteName || '';
      const siteB = b.siteName || '';
      if (siteA.localeCompare(siteB) !== 0) {
        return siteA.localeCompare(siteB);
      }
      const statusA = statusOrder[a.status] || 99;
      const statusB = statusOrder[b.status] || 99;
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      if (a.type === 'gateway' && b.type !== 'gateway') { return -1; }
      if (a.type !== 'gateway' && b.type === 'gateway') { return 1; }
      const nameA = a.deviceName || a.gatewayEui || '';
      const nameB = b.deviceName || b.gatewayEui || '';
      return nameA.localeCompare(nameB);
    });
  }

  private getDeviceStatusColor(status: string): string {
    if (status === 'fault') { return '#d10000'; }
    if (status === 'inactive') { return '#9e9e9e'; }
    return '#4CAF50';
  }

  private getGatewayStatusColor(status?: string): string {
    if (status === 'fault') { return '#d10000'; }
    if (status === 'inactive') { return '#9e9e9e'; }
    return '#4CAF50';
  }

  private getSpreadingFactorColor(spreadingFactor: number): string {
    if (spreadingFactor && (spreadingFactor >= 7 || spreadingFactor <= 12)) {
      const colorInfo = this.spreadingFactorColors.find((item) => item.sf === spreadingFactor);
      return colorInfo ? colorInfo.color : '#FFFFFF';
    }
    return 'rgba(255,255,255,0.6)';
  }

  private getContrastingTextColor(hexColor: string): string {
    if (hexColor.startsWith('#')) {
      hexColor = hexColor.slice(1);
    }
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance > 186 ? '#868686' : '#ffffff';
  }
}
